// src/components/EditTripModal.jsx
import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import "./css/EditTripModal.css";

// Optional Firebase modular imports - only used if `db` prop is provided.
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    deleteDoc,
    orderBy,
    limit,
    updateDoc,
} from "firebase/firestore";
import { firebaseApp } from "../firebase";

// Optional storage imports - only used if `storage` prop is provided.
import { ref as storageRef, deleteObject, getStorage } from "firebase/storage";

/**
 * EditTripModal (handles-only mode)
 *
 * Props:
 * - open, trip, ownerUid, db, storage, resolveHandleToUid, resolveUidToHandle, onSave, onClose/onCancel, saving
 */
export default function EditTripModal({
    open,
    trip = null,
    ownerUid = null,
    db = null,
    storage = null,
    resolveHandleToUid = null,
    resolveUidToHandle = null,
    onClose = undefined,
    onCancel = undefined,
    onSave = async (updatedFields) => { },
    saving = false,
}) {
    // ------------------------
    // Hooks (top-level)
    // ------------------------
    const [form, setForm] = useState({
        trip_id: "",
        title: "",
        startDate: "",
        endDate: "",
        startLocation: "",
        destination: "",
        notes: "",
        visibility: "public",
        allowedUsersText: "",
    });

    // validation errors: { fieldName: "message" }
    const [errors, setErrors] = useState({});

    // TAGS
    const MAX_TAGS = 10;
    const MAX_TAG_LENGTH = 30;
    const suggestedTags = [
        "roadtrip", "mountains", "beaches", "food", "photography", "bike", "hiking", "camping", "adventure", "friends",
    ];

    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [tagError, setTagError] = useState(null);

    const [customMapSrc, setCustomMapSrc] = useState("");
    const [resolvedHandles, setResolvedHandles] = useState([]);
    const [invalidTokens, setInvalidTokens] = useState([]);
    const [resolvingAllowedUsers, setResolvingAllowedUsers] = useState(false);

    const [deleting, setDeleting] = useState(false);

    const resolveTimer = useRef(null);

    // ---------- New: allowed-user autocomplete state ----------
    const [allowedQuery, setAllowedQuery] = useState(""); // current token search (no @)
    const [allowedSuggestions, setAllowedSuggestions] = useState([]); // [{ handle, uid, displayName?, meta? }]
    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestionsTimer = useRef(null);

    // focus refs for quick focus on first invalid field
    const titleRef = useRef(null);
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);
    const startLocationRef = useRef(null);
    const destinationRef = useRef(null);

    // ------------------------
    // Helpers
    // ------------------------
    const update = (patch) => setForm((s) => ({ ...s, ...patch }));

    const handleClose = () => {
        if (typeof onClose === "function") return onClose();
        if (typeof onCancel === "function") return onCancel();
        return undefined;
    };

    const parseTokens = (text) => {
        if (!text) return [];
        return text
            .split(/[\n,]+/)
            .map((t) => t.trim())
            .filter(Boolean);
    };

    const stripAt = (s) => (s && s.startsWith("@") ? s.slice(1) : s);

    // VALIDATION ------------------------------------------------
    const validate = (values = form) => {
        const e = {};

        if (!values.title || !String(values.title).trim()) {
            e.title = "Title is required.";
        }

        if (!values.startDate) {
            e.startDate = "Start date is required.";
        }

        if (!values.endDate) {
            e.endDate = "End date is required.";
        }

        // optional: ensure startDate <= endDate if both present
        if (values.startDate && values.endDate) {
            try {
                const s = new Date(values.startDate);
                const en = new Date(values.endDate);
                if (!isNaN(s.getTime()) && !isNaN(en.getTime()) && s > en) {
                    e.endDate = "End date must be the same or after start date.";
                }
            } catch (err) { /* ignore */ }
        }

        if (!values.startLocation || !String(values.startLocation).trim()) {
            e.startLocation = "Start location is required.";
        }

        if (!values.destination || !String(values.destination).trim()) {
            e.destination = "Destination is required.";
        }

        // When visibility restricted, ensure resolved handles exist (optional; keep original invalidTokens check)
        if (values.visibility === "restricted") {
            // invalidTokens handled elsewhere; if there are invalid tokens, add an error
            if (invalidTokens && invalidTokens.length > 0) {
                e.allowedUsersText = "Remove or fix invalid tokens (only @handles allowed).";
            }
        }

        return e;
    };

    // run live validation on certain changes (title, dates, start/destination)
    useEffect(() => {
        const e = validate();
        setErrors(e);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.title, form.startDate, form.endDate, form.startLocation, form.destination, invalidTokens, form.visibility]);

    const focusFirstError = (errObj) => {
        if (!errObj || Object.keys(errObj).length === 0) return;
        if (errObj.title && titleRef.current) return titleRef.current.focus();
        if (errObj.startDate && startDateRef.current) return startDateRef.current.focus();
        if (errObj.endDate && endDateRef.current) return endDateRef.current.focus();
        if (errObj.startLocation && startLocationRef.current) return startLocationRef.current.focus();
        if (errObj.destination && destinationRef.current) return destinationRef.current.focus();
        // fallback: focus modal close button if available via handleClose not available
    };

    // Tag helpers
    const sanitizeTag = (raw) => {
        if (typeof raw !== "string") return "";
        return raw.trim().toLowerCase().replace(/[^a-z0-9-_ ]+/g, "").slice(0, MAX_TAG_LENGTH);
    };

    const addTag = (raw) => {
        setTagError(null);
        if (!raw) return;
        const t = sanitizeTag(raw);
        if (!t) return;
        if (tags.includes(t)) {
            setTagError("Tag already added.");
            return;
        }
        if (tags.length >= MAX_TAGS) {
            setTagError(`You can add at most ${MAX_TAGS} tags.`);
            return;
        }
        setTags((s) => [...s, t]);
        setTagInput("");
    };

    const removeTag = (t) => setTags((s) => s.filter((x) => x !== t));

    const onTagKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            if (tagInput) addTag(tagInput);
        } else if (e.key === "Backspace" && !tagInput && tags.length) {
            setTags((s) => s.slice(0, -1));
        }
    };

    // ------------------------
    // resolve handle uid helpers (unchanged, copied from your original)
    // ------------------------
    const defaultResolveHandleToUid = async (handleNoAt) => {
        if (!db || !handleNoAt) return null;
        const sanitize = (s) =>
            String(s || "").replace(/[\u200B-\u200D\uFEFF]/g, "").trim().normalize("NFKC").toLowerCase();
        const key = sanitize(handleNoAt);
        if (!key) return null;

        try {
            const docRef = doc(db, "handles", key);
            const snap = await getDoc(docRef);
            if (snap && (typeof snap.exists === "function" ? snap.exists() : snap.exists)) {
                const data = snap.data();
                const uid = data?.uid || data?.userId || data?.id || null;
                return uid;
            }
        } catch (err) { /* ignore */ }

        try {
            const q = query(collection(db, "handles"), where("handle", "==", key));
            const snaps = await getDocs(q);
            if (!snaps.empty) {
                const d = snaps.docs[0].data();
                return d?.uid || d?.userId || d?.id || null;
            }
        } catch (err) { /* ignore */ }

        try {
            const q2 = query(collection(db, "users"), where("handle", "==", key));
            const snaps2 = await getDocs(q2);
            if (!snaps2.empty) {
                const d = snaps2.docs[0].data();
                return d?.uid || d?.id || null;
            }
        } catch (err) { /* ignore */ }

        return null;
    };

    const defaultResolveUidToHandle = async (uid) => {
        if (!db || !uid) return null;
        try {
            const q = query(collection(db, "handles"), where("uid", "==", uid));
            const snaps = await getDocs(q);
            if (!snaps.empty) {
                const docRef = snaps.docs[0];
                if (docRef.id) return docRef.id;
                const dd = docRef.data();
                if (dd && (dd.handle || dd.name)) return dd.handle || dd.name;
            }
        } catch (err) { /* ignore */ }

        try {
            const d = await getDoc(doc(db, "users", uid));
            if (d && (typeof d.exists === "function" ? d.exists() : d.exists)) {
                const data = d.data();
                if (data && (data.handle || data.username)) return data.handle || data.username;
            }
        } catch (err) { /* ignore */ }

        try {
            const q2 = query(collection(db, "users"), where("uid", "==", uid));
            const snaps2 = await getDocs(q2);
            if (!snaps2.empty) {
                const dd = snaps2.docs[0].data();
                if (dd && (dd.handle || dd.username)) return dd.handle || dd.username;
            }
        } catch (err) { /* ignore */ }

        return null;
    };

    const resolveHandleToUidFn = typeof resolveHandleToUid === "function" ? resolveHandleToUid : defaultResolveHandleToUid;
    const resolveUidToHandleFn = typeof resolveUidToHandle === "function" ? resolveUidToHandle : defaultResolveUidToHandle;

    // ------------------------
    // Effects to initialize + resolve allowed users (mostly unchanged)
    // ------------------------
    useEffect(() => {
        if (!db || !open) return;
        (async () => {
            try {
                const key = "eric";
                await getDoc(doc(db, "handles", key));
                await getDocs(query(collection(db, "handles"), where("handle", "==", key)));
                await getDocs(query(collection(db, "users"), where("handle", "==", key)));
            } catch (err) {
                console.error("debug handle lookup error:", err);
            }
        })();
    }, [db, open]);

    useEffect(() => {
        if (!trip) {
            setForm({
                trip_id: "",
                title: "",
                startDate: "",
                endDate: "",
                startLocation: "",
                destination: "",
                notes: "",
                visibility: "public",
                allowedUsersText: "",
            });
            setCustomMapSrc("");
            setResolvedHandles([]);
            setInvalidTokens([]);
            setResolvingAllowedUsers(false);
            setTags([]);
            setTagInput("");
            setTagError(null);
            setErrors({});
            return;
        }

        const allowedArr = Array.isArray(trip.allowedUsers)
            ? trip.allowedUsers
            : Array.isArray(trip.allowed_users)
                ? trip.allowed_users
                : [];

        const filtered = allowedArr.filter((a) => a && a !== ownerUid);

        setResolvingAllowedUsers(true);
        setResolvedHandles([]);
        setInvalidTokens([]);

        (async () => {
            try {
                const results = await Promise.all(
                    filtered.map(async (tok) => {
                        if (typeof tok === "string" && tok.startsWith("@")) {
                            const h = stripAt(tok);
                            return { original: tok, display: `@${h}`, handle: h, uid: null, status: "loading" };
                        }

                        if (typeof tok === "string") {
                            try {
                                const handle = await resolveUidToHandleFn(tok);
                                if (handle) {
                                    return { original: tok, display: `@${handle}`, handle, uid: tok, status: "resolved" };
                                } else {
                                    return { original: tok, display: null, handle: null, uid: tok, status: "notfound" };
                                }
                            } catch (err) {
                                return { original: tok, display: null, handle: null, uid: tok, status: "notfound" };
                            }
                        }

                        return { original: tok, display: null, handle: null, uid: tok, status: "notfound" };
                    })
                );

                const displayList = results.filter((r) => r.display).map((r) => r.display);
                setForm((s) => ({ ...s, allowedUsersText: displayList.join(", ") }));

                setResolvedHandles(
                    results.map((r) => {
                        if (r.display) {
                            return {
                                handle: r.handle || stripAt(r.display),
                                uid: r.uid || null,
                                status: r.status === "resolved" ? "resolved" : "loading",
                                original: r.display,
                            };
                        }
                        return {
                            handle: null,
                            uid: r.uid || null,
                            status: r.status || "notfound",
                            original: r.original,
                        };
                    })
                );

                const invalids = results.filter((r) => !r.display).map((r) => r.original);
                setInvalidTokens(invalids);
            } catch (err) {
                setForm((s) => ({ ...s, allowedUsersText: "" }));
                setResolvedHandles([]);
                setInvalidTokens([]);
            } finally {
                setResolvingAllowedUsers(false);
            }
        })();

        setForm((s) => ({
            ...s,
            trip_id: trip.trip_id || trip.id || "",
            title: trip.title || "",
            startDate: trip.startDate || trip.start_date || "",
            endDate: trip.endDate || trip.end_date || "",
            startLocation: trip.startLocation || trip.start_location || "",
            destination: trip.destination || trip.dest || "",
            notes: trip.notes || trip.tripNotes || "",
            visibility: trip.visibility || (trip.private ? "private" : "public"),
        }));

        const pref = trip.mapIframeSrc || trip.map_iframe_src || "";
        setCustomMapSrc(pref);

        // initialize tags (best-effort)
        const fromTags = Array.isArray(trip.tags)
            ? trip.tags
            : Array.isArray(trip.tags_list)
                ? trip.tags_list
                : Array.isArray(trip.tagsArray)
                    ? trip.tagsArray
                    : [];
        const normalizedTags = (fromTags || []).map((t) => sanitizeTag(String(t))).filter(Boolean).slice(0, MAX_TAGS);
        setTags(normalizedTags);
        setErrors({});
    }, [trip, ownerUid, resolveUidToHandle]);

    // When user edits the textarea, debounce and attempt to resolve handles -> UIDs.
    useEffect(() => {
        if (resolveTimer.current) clearTimeout(resolveTimer.current);
        const tokens = parseTokens(form.allowedUsersText);

        const invalid = [];
        const preview = tokens.map((tok) => {
            if (!tok.startsWith("@")) {
                invalid.push(tok);
                return { handle: null, uid: null, status: "invalid", original: tok };
            }
            const h = stripAt(tok);
            return { handle: h, uid: null, status: "loading", original: tok };
        });

        setResolvedHandles(preview);
        setInvalidTokens(invalid);

        if (!resolveHandleToUidFn) {
            setResolvedHandles((prev) => prev.map((p) => (p.status === "loading" ? { ...p, status: "notfound" } : p)));
            setInvalidTokens((prev) => [
                ...new Set([...prev, ...preview.filter((p) => p.status === "loading").map((p) => p.original)]),
            ]);
            return;
        }

        resolveTimer.current = setTimeout(async () => {
            setResolvingAllowedUsers(true);
            const toResolve = preview.filter((p) => p.status === "loading");

            const results = await Promise.all(
                toResolve.map(async (p) => {
                    try {
                        const uid = await resolveHandleToUidFn(p.handle);
                        if (uid) return { ...p, uid, status: "resolved" };
                        return { ...p, uid: null, status: "notfound" };
                    } catch (err) {
                        return { ...p, uid: null, status: "notfound" };
                    }
                })
            );

            const merged = preview.map((p) => {
                const r = results.find((res) => res.original === p.original);
                return r || p;
            });

            setResolvedHandles(merged);

            const invalidAfter = merged.filter((m) => m.status === "invalid" || m.status === "notfound").map((m) => m.original);
            setInvalidTokens(invalidAfter);

            setResolvingAllowedUsers(false);
        }, 350);

        return () => {
            if (resolveTimer.current) clearTimeout(resolveTimer.current);
        };
    }, [form.allowedUsersText]);

    // ------------------------
    // ---------- New: suggestions fetch (prefix search) ----------
    // ------------------------
    useEffect(() => {
        // find last token in allowedUsersText; if it begins with '@' and has at least 1 char, set allowedQuery
        const tokens = parseTokens(form.allowedUsersText);
        const last = tokens.length ? tokens[tokens.length - 1] : "";
        if (last && last.startsWith("@")) {
            const q = stripAt(last).trim().toLowerCase();
            setAllowedQuery(q);
        } else {
            setAllowedQuery("");
            setAllowedSuggestions([]);
            setShowSuggestions(false);
        }
    }, [form.allowedUsersText]);

    useEffect(() => {
        if (!db) return;
        if (!allowedQuery || allowedQuery.length === 0) {
            setAllowedSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        // debounce
        if (suggestionsTimer.current) clearTimeout(suggestionsTimer.current);
        suggestionsTimer.current = setTimeout(async () => {
            try {
                // Firestore prefix query using range (handle >= query && handle <= query + '\uf8ff')
                // ensure 'handle' field is normalized/stored lowercase in your DB for this to be effective
                const start = allowedQuery;
                const end = allowedQuery + "\uf8ff";
                const col = collection(db, "handles");
                const q = query(col, where("handle", ">=", start), where("handle", "<=", end), orderBy("handle"), limit(8));
                const snaps = await getDocs(q);

                const list = snaps.docs.map((d) => {
                    const data = d.data();
                    return {
                        handle: d.id || data.handle || data.name || "",
                        uid: data?.uid || data?.userId || data?.id || null,
                        meta: data?.displayName || data?.name || "",
                    };
                });
                setAllowedSuggestions(list);
                setShowSuggestions(list.length > 0);
            } catch (err) {
                console.warn("allowed suggestions failed", err);
                setAllowedSuggestions([]);
                setShowSuggestions(false);
            }
        }, 220);

        return () => {
            if (suggestionsTimer.current) clearTimeout(suggestionsTimer.current);
        };
    }, [allowedQuery, db]);

    const pickAllowedSuggestion = (sugg) => {
        if (!sugg || !sugg.handle) return;
        // replace the last token in the allowedUsersText with @handle
        const tokens = parseTokens(form.allowedUsersText);
        if (tokens.length === 0) {
            tokens.push("@" + sugg.handle);
        } else {
            tokens[tokens.length - 1] = "@" + sugg.handle;
        }
        const newText = tokens.join(", ");
        setForm((s) => ({ ...s, allowedUsersText: newText }));
        // update resolvedHandles optimistic entry
        setResolvedHandles((prev) => {
            const withoutLast = prev.slice(0, Math.max(0, prev.length - 1));
            return [
                ...withoutLast,
                { original: `@${sugg.handle}`, display: `@${sugg.handle}`, handle: sugg.handle, uid: sugg.uid, status: sugg.uid ? "resolved" : "notfound" },
            ];
        });
        setAllowedSuggestions([]);
        setShowSuggestions(false);
    };

    // ------------------------
    // Delete helpers (unchanged)
    // ------------------------
    const extractStoragePathsFromTrip = (t) => {
        if (!t) return [];
        const paths = new Set();
        if (Array.isArray(t.storagePaths)) t.storagePaths.forEach((p) => p && paths.add(String(p)));
        if (Array.isArray(t.attachments)) {
            t.attachments.forEach((a) => {
                if (!a) return;
                if (typeof a === "string") paths.add(a);
                else if (a.path) paths.add(a.path);
                else if (a.storagePath) paths.add(a.storagePath);
            });
        }
        if (Array.isArray(t.media)) {
            t.media.forEach((m) => {
                if (!m) return;
                if (typeof m === "string") paths.add(m);
                else if (m.path) paths.add(m.path);
                else if (m.storagePath) paths.add(m.storagePath);
                else if (m.url && m.url.startsWith("gs://")) {
                    const match = m.url.match(/^gs:\/\/[^\/]+\/(.+)$/);
                    if (match && match[1]) paths.add(match[1]);
                }
            });
        }
        if (Array.isArray(t.images)) {
            t.images.forEach((img) => {
                if (!img) return;
                if (typeof img === "string") paths.add(img);
                else if (img.path) paths.add(img.path);
            });
        }
        if (t.storagePath) paths.add(t.storagePath);
        if (t.mapStoragePath) paths.add(t.mapStoragePath);
        return Array.from(paths).filter(Boolean).map((p) => String(p));
    };
    // Helper: extract storage path from a firebase download URL (best-effort)
    function extractPathFromFirebaseDownloadUrl(downloadUrl) {
        if (!downloadUrl || typeof downloadUrl !== "string") return null;
        try {
            const m = downloadUrl.match(/\/o\/([^?]+)/);
            if (!m || !m[1]) return null;
            return decodeURIComponent(m[1]);
        } catch (e) {
            return null;
        }
    }
    // place near top of file or above deleteMediaDocsForTrip
    async function tryDeleteStorageObject(candidatePath, providedStorage) {
        if (!candidatePath) return false;

        // normalize candidate path: remove gs://bucket/ prefix and leading slashes
        let path = String(candidatePath);
        try {
            const gsMatch = path.match(/^gs:\/\/[^\/]+\/(.+)$/);
            if (gsMatch && gsMatch[1]) path = gsMatch[1];
            // also handle firebase download URL /o/<encodedPath>
            const httpMatch = path.match(/\/o\/([^?\/]+)/);
            if (httpMatch && httpMatch[1]) {
                try { path = decodeURIComponent(httpMatch[1]); } catch (e) { path = httpMatch[1]; }
            }
            path = path.replace(/^\/+/, ""); // remove leading slash(es)
        } catch (e) {
            // fallback keep original
        }

        // Resolve a Storage instance: prefer providedStorage, else try getStorage(firebaseApp)
        let effStorage = providedStorage || null;
        if (!effStorage) {
            try {
                // if you have a custom bucket name stored somewhere, pass it as second arg
                // e.g. getStorage(firebaseApp, "gs://travel-6c761.firebasestorage.app")
                effStorage = getStorage ? getStorage(firebaseApp) : null;
            } catch (e) {
                effStorage = null;
            }
        }

        if (!effStorage) {
            console.warn("No Firebase Storage instance available to delete:", candidatePath);
            return false;
        }

        try {
            const r = storageRef(effStorage, path);
            await deleteObject(r);
            return true;
        } catch (err) {
            console.warn("Failed to delete media storage object", candidatePath, err);
            return false;
        }
    }

    /**
     * Delete media docs under trips/{tripId}/media AND their storage objects (main + thumbnail).
     * Returns an object with stats { mediaDocs: n, storageDeletesAttempted: m, errors: [...] }
     */
    const deleteMediaDocsForTrip = async (tripId) => {
        const stats = { mediaDocs: 0, storageDeletesAttempted: 0, errors: [] };
        if (!db || !tripId) return stats;

        try {
            const mediaCol = collection(db, "trips", tripId, "media");
            const snaps = await getDocs(mediaCol);
            if (snaps && !snaps.empty) {
                const deletions = [];
                for (const d of snaps.docs) {
                    const data = d.data() || {};
                    stats.mediaDocs += 1;

                    // collect candidate paths to delete
                    const candidates = new Set();
                    if (data.storagePath) candidates.add(String(data.storagePath));
                    if (data.thumbnailPath) candidates.add(String(data.thumbnailPath));
                    if (data.path) candidates.add(String(data.path));
                    if (data.thumbPath) candidates.add(String(data.thumbPath));
                    if (data.url && typeof data.url === "string") {
                        const ex = extractPathFromFirebaseDownloadUrl(data.url);
                        if (ex) candidates.add(ex);
                    }
                    if (data.thumbnailUrl && typeof data.thumbnailUrl === "string") {
                        const ex2 = extractPathFromFirebaseDownloadUrl(data.thumbnailUrl);
                        if (ex2) candidates.add(ex2);
                    }

                    // attempt storage deletes in parallel
                    // inside the loop where you built `candidates` set
                    const storageDeletes = Array.from(candidates).map(async (p) => {
                        if (!p) return;
                        try {
                            stats.storageDeletesAttempted += 1;
                            // use helper that resolves storage instance safely
                            await tryDeleteStorageObject(p, storage); // `storage` is your component prop
                        } catch (err) {
                            stats.errors.push({ type: "storage", path: p, message: err?.message || String(err) });
                            console.warn("Failed to delete media storage object", p, err);
                        }
                    });


                    // once storage deletes triggered, delete the Firestore doc
                    deletions.push(
                        Promise.all(storageDeletes)
                            .catch(() => { }) // swallow internal storage errors so doc delete still attempted
                            .then(async () => {
                                try {
                                    await deleteDoc(doc(db, "trips", tripId, "media", d.id));
                                } catch (err) {
                                    stats.errors.push({ type: "firestore-doc", docId: d.id, message: err?.message || String(err) });
                                    console.warn("Failed to delete media doc", d.id, err);
                                }
                            })
                    );
                }

                // wait for all media doc deletions/storage deletions to complete
                await Promise.all(deletions);
            }
        } catch (err) {
            stats.errors.push({ type: "list-media", message: err?.message || String(err) });
            console.error("Failed enumerating media docs for trip:", err);
        }

        return stats;
    };

    // Replace your existing confirmAndDelete with this version (it calls deleteMediaDocsForTrip)
    const confirmAndDelete = async () => {
        if (!trip) return;
        const confirmed = window.confirm("Delete this trip? This will permanently remove the trip record and any associated storage files (if available). This action cannot be undone.");
        if (!confirmed) return;
        setDeleting(true);

        try {
            // 1) Delete media subcollection and associated storage objects
            if (db && trip.trip_id) {
                try {
                    const mediaStats = await deleteMediaDocsForTrip(trip.trip_id);
                    if (mediaStats && mediaStats.errors && mediaStats.errors.length > 0) {
                        console.warn("Some media deletions failed:", mediaStats.errors);
                    }
                } catch (err) {
                    console.warn("deleteMediaDocsForTrip failed:", err);
                }
            }

            // 2) Delete any other storage paths referenced directly on the trip object
            if (storage) {
                const paths = extractStoragePathsFromTrip(trip);
                if (paths.length > 0) {
                    await Promise.all(paths.map(async (p) => {
                        try {
                            let candidate = p;
                            // support gs:// URIs
                            const gsMatch = String(p).match(/^gs:\/\/[^\/]+\/(.+)$/);
                            if (gsMatch && gsMatch[1]) candidate = gsMatch[1];
                            // support a firebase download URL /o/<encodedPath>
                            const httpMatch = String(p).match(/\/o\/([^?\/]+)/);
                            if (httpMatch && httpMatch[1]) {
                                try { candidate = decodeURIComponent(httpMatch[1]); } catch (er) { candidate = httpMatch[1]; }
                            }
                            const r = storageRef(storage, candidate);
                            await deleteObject(r);
                        } catch (err) {
                            console.warn("Failed to delete storage object referenced on trip:", p, err);
                        }
                    }));
                }
            }

            // 3) Delete the trip doc itself
            if (db && trip) {
                const tripId = trip.trip_id || trip.id || trip._id;
                if (tripId) {
                    try {
                        await deleteDoc(doc(db, "trips", tripId));
                    } catch (err) {
                        console.error("Failed to delete trip doc:", err);
                        throw err;
                    }
                } else {
                    // fallback: delete by slug + owner_id if present
                    if (trip.slug && trip.owner_id && db) {
                        try {
                            const q = query(collection(db, "trips"), where("slug", "==", trip.slug), where("owner_id", "==", trip.owner_id));
                            const snaps = await getDocs(q);
                            await Promise.all(snaps.docs.map((d) => deleteDoc(doc(db, "trips", d.id))));
                        } catch (err) {
                            console.error("Failed to delete trip by slug/owner fallback:", err);
                        }
                    } else {
                        console.warn("No trip id present; unable to delete Firestore doc.");
                    }
                }
            }

            // 4) Emit event and close modal (same behavior you had)
            const finalTripId = trip.trip_id || trip.id || trip._id || (trip.slug ? trip.slug : null);

            try {
                window.dispatchEvent(
                    new CustomEvent("trip:deleted", {
                        detail: {
                            trip_id: finalTripId,
                            id: trip.id || null,
                            slug: trip.slug || null,
                            docId: trip.trip_id || trip.id || trip._id || null
                        }
                    })
                );
            } catch (e) { /* ignore */ }

            // keep the old duplicate dispatch (you had it)
            try { window.dispatchEvent(new CustomEvent("trip:deleted", { detail: { trip_id: finalTripId } })); } catch (e) { }

            handleClose();
        } catch (err) {
            console.error("Delete trip failed:", err);
            window.alert("Failed to delete trip. Check console for details.");
        } finally {
            setDeleting(false);
        }
    };


    // ------------------------
    // Submit handler (unchanged semantics; ensures allowedUsers include uids)
    // ------------------------
    const handleSubmit = async (e) => {
        e?.preventDefault?.();

        // run validation
        const validation = validate();
        setErrors(validation);

        if (Object.keys(validation).length > 0) {
            focusFirstError(validation);
            return;
        }

        if (!form.title?.trim()) {
            return alert("Please provide a title for the trip.");
        }

        if (invalidTokens && invalidTokens.length > 0) {
            return alert("Please remove or fix invalid tokens (only @handles are allowed).");
        }

        if (tags.length > MAX_TAGS) {
            return alert(`You can add at most ${MAX_TAGS} tags.`);
        }

        const payload = {
            trip_id: form.trip_id,
            title: form.title.trim(),
            startDate: form.startDate || null,
            endDate: form.endDate || null,
            startLocation: form.startLocation || null,
            destination: form.destination || null,
            notes: form.notes || null,
            visibility: form.visibility || "public",
        };

        if (tags && tags.length > 0) payload.tags = tags.slice(0, MAX_TAGS);
        else payload.tags = [];

        if (form.visibility === "restricted") {
            const uidSet = new Set();
            if (ownerUid) uidSet.add(ownerUid);

            for (const r of resolvedHandles) {
                if (!r || !r.original) continue;
                if (r.original.startsWith("@") && r.status === "resolved" && r.uid) {
                    uidSet.add(r.uid);
                } else {
                    // Attempt a last-resort resolution for plain handles in case user typed @handle but resolution didn't occur earlier
                    if (r.original && r.original.startsWith("@") && r.status !== "resolved") {
                        try {
                            const h = stripAt(r.original);
                            const uid = await resolveHandleToUidFn(h);
                            if (uid) uidSet.add(uid);
                        } catch (err) { /* ignore */ }
                    }
                }
            }

            payload.allowedUsers = Array.from(uidSet);
        } else {
            payload.allowedUsers = [];
        }

        try {
            await onSave(payload);
        } catch (err) {
            console.error("EditTripModal onSave error:", err);
        }
    };

    if (!open) return null;

    // map helpers unchanged...
    const buildAutoMapSrc = () => {
        const origin = (form.startLocation || "").trim();
        const dest = (form.destination || "").trim();
        const qEncode = (s) => encodeURIComponent(s);

        if (dest) return `https://www.google.com/maps?q=${qEncode(dest)}&output=embed`;
        if (origin) return `https://www.google.com/maps?q=${qEncode(origin)}&output=embed`;
        if (origin || dest) {
            const combined = `${origin} to ${dest}`.trim();
            if (combined) return `https://www.google.com/maps?q=${qEncode(combined)}&output=embed`;
        }
        return null;
    };

    const extractSrcFromIframe = (input) => {
        if (!input) return "";
        const match = input.match(/src=(?:"|')([^"']+)(?:"|')/i);
        if (match && match[1]) return match[1];
        if (/^https?:\/\//i.test(input.trim())) return input.trim();
        return "";
    };

    const finalMapSrc = (() => {
        const extracted = extractSrcFromIframe(customMapSrc);
        if (extracted) return extracted;
        return buildAutoMapSrc();
    })();

    const clearCustomMap = () => setCustomMapSrc("");

    // Render helpers unchanged...
    const renderResolvedPreview = () => {
        if (form.visibility !== "restricted") return null;

        const labelFor = (t) => {
            if (t && t.handle) return `@${t.handle}`;
            if (t && t.original && String(t.original).startsWith("@")) return t.original;
            return t && t.original ? String(t.original) : "@unknown";
        };

        return (
            <div style={{ marginTop: 8 }}>
                {resolvingAllowedUsers ? (
                    <div style={{ color: "#9aa0b4", fontSize: 13 }}>Resolving handles… (only @handles allowed)</div>
                ) : null}

                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                    {resolvedHandles.length === 0 ? (
                        <div className="muted" style={{ marginTop: 8 }}>No handles entered.</div>
                    ) : (
                        resolvedHandles.map((t, i) => {
                            if (!t) return null;
                            const key = `${t.original || t.handle || i}-${i}`;

                            const state = t.status === "resolved" && t.uid ? "resolved"
                                : t.status === "loading" ? "loading"
                                    : (t.status === "notfound" || t.status === "invalid") ? "invalid"
                                        : "unknown";

                            const label = labelFor(t) + (state === "loading" ? " (resolving…)" : (state === "invalid" ? " (not found)" : ""));

                            return (
                                <div
                                    key={key}
                                    className="handle-chip"
                                    data-state={state}
                                    title={state === "resolved" ? "Resolved" : undefined}
                                    style={{ fontSize: 13 }}
                                    aria-live="polite"
                                >
                                    {label}
                                </div>
                            );
                        })
                    )}
                </div>

                {invalidTokens && invalidTokens.length > 0 ? (
                    <div className="invalid-tokens">
                        Invalid entries: {invalidTokens.join(", ")} — only <strong>@handles</strong> are allowed (remove or fix them).
                    </div>
                ) : null}
            </div>
        );
    };
    // place this right before the JSX/render section (before "if (!open) return null;" or just above the return)
    const modalTitle = (() => {
        const candidate = form.title || (trip && (trip.title || trip.name)) || "";
        if (candidate && String(candidate).trim()) {
            return `Edit - ${String(candidate).trim()}`;
        }
        return "Edit Trip";
    })();

    // ------------------------
    // JSX
    // ------------------------
    return (
        <Modal title={modalTitle} onClose={handleClose} aria-label={modalTitle}>
            <form className="edit-trip-form" onSubmit={handleSubmit} noValidate>
                <label className="field">
                    <div className="label">Title</div>
                    <input
                        ref={titleRef}
                        value={form.title}
                        onChange={(e) => update({ title: e.target.value })}
                        required
                        placeholder="Trip title"
                        aria-invalid={Boolean(errors.title)}
                        aria-describedby={errors.title ? "error-title" : undefined}
                    />
                    {errors.title ? (
                        <div id="error-title" className="field-error" role="alert">{errors.title}</div>
                    ) : null}
                </label>

                <div style={{ display: "flex", gap: 8 }}>
                    <label className="field" style={{ flex: 1 }}>
                        <div className="label">Start date</div>
                        <input
                            ref={startDateRef}
                            type="date"
                            value={form.startDate || ""}
                            onChange={(e) => update({ startDate: e.target.value })}
                            aria-invalid={Boolean(errors.startDate)}
                            aria-describedby={errors.startDate ? "error-start-date" : undefined}
                        />
                        {errors.startDate ? (
                            <div id="error-start-date" className="field-error" role="alert">{errors.startDate}</div>
                        ) : null}
                    </label>

                    <label className="field" style={{ flex: 1 }}>
                        <div className="label">End date</div>
                        <input
                            ref={endDateRef}
                            type="date"
                            value={form.endDate || ""}
                            onChange={(e) => update({ endDate: e.target.value })}
                            aria-invalid={Boolean(errors.endDate)}
                            aria-describedby={errors.endDate ? "error-end-date" : undefined}
                        />
                        {errors.endDate ? (
                            <div id="error-end-date" className="field-error" role="alert">{errors.endDate}</div>
                        ) : null}
                    </label>
                </div>

                <div className="inline-pair" style={{ display: "flex", gap: 8 }}>
                    <label className="field" style={{ flex: 1, minWidth: 0 }}>
                        <div className="label">Start location</div>
                        <input
                            ref={startLocationRef}
                            value={form.startLocation || ""}
                            onChange={(e) => update({ startLocation: e.target.value })}
                            placeholder="City, station, etc."
                            aria-invalid={Boolean(errors.startLocation)}
                            aria-describedby={errors.startLocation ? "error-start-location" : undefined}
                        />
                        {errors.startLocation ? (
                            <div id="error-start-location" className="field-error" role="alert">{errors.startLocation}</div>
                        ) : null}
                    </label>

                    <label className="field" style={{ flex: 1, minWidth: 0 }}>
                        <div className="label">Destination</div>
                        <input
                            ref={destinationRef}
                            value={form.destination || ""}
                            onChange={(e) => update({ destination: e.target.value })}
                            placeholder="City, station, etc."
                            aria-invalid={Boolean(errors.destination)}
                            aria-describedby={errors.destination ? "error-destination" : undefined}
                        />
                        {errors.destination ? (
                            <div id="error-destination" className="field-error" role="alert">{errors.destination}</div>
                        ) : null}
                    </label>
                </div>

                <label className="field">
                    <div className="label">Visibility</div>
                    <select value={form.visibility} onChange={(e) => update({ visibility: e.target.value })}>
                        <option value="public">Public</option>
                        <option value="restricted">Restricted</option>
                        <option value="private">Private</option>
                    </select>
                </label>

                {form.visibility === "restricted" && (
                    <label className="field allowed-suggestions">
                        <div className="label">Allowed users</div>
                        <small className="muted" style={{ display: "block", marginBottom: 6 }}>
                            Enter <strong>handles with leading "@"</strong> only (example: <code>@alice, @bob</code>).
                            You can search by typing <code>@</code> + letters — suggestions will appear.
                        </small>

                        <textarea
                            value={form.allowedUsersText || ""}
                            onChange={(e) => {
                                update({ allowedUsersText: e.target.value });
                                // show suggestions only if last token starts with @
                                const tokens = parseTokens(e.target.value);
                                const last = tokens.length ? tokens[tokens.length - 1] : "";
                                setShowSuggestions(Boolean(last && last.startsWith("@")));
                            }}
                            placeholder='@alice, @bob (only handles with "@")'
                            rows={3}
                            aria-invalid={Boolean(errors.allowedUsersText)}
                            aria-describedby={errors.allowedUsersText ? "error-allowed-users" : undefined}
                        />
                        {errors.allowedUsersText ? (
                            <div id="error-allowed-users" className="field-error" role="alert">{errors.allowedUsersText}</div>
                        ) : null}

                        {/* suggestions dropdown */}
                        {showSuggestions && allowedSuggestions && allowedSuggestions.length > 0 && (
                            <div className="allowed-suggestions-list" role="listbox" aria-label="Handle suggestions">
                                {allowedSuggestions.map((s) => (
                                    <div
                                        key={s.handle}
                                        className="allowed-suggestion"
                                        role="option"
                                        tabIndex={0}
                                        onClick={() => pickAllowedSuggestion(s)}
                                        onKeyDown={(e) => { if (e.key === "Enter") pickAllowedSuggestion(s); }}
                                    >
                                        <div className="suggestion-handle">@{s.handle}</div>
                                        {s.meta ? <div className="suggestion-meta">{s.meta}</div> : null}
                                    </div>
                                ))}
                            </div>
                        )}

                        {renderResolvedPreview()}
                    </label>
                )}

                <label className="field">
                    <div className="label">Notes</div>
                    <textarea
                        value={form.notes || ""}
                        onChange={(e) => update({ notes: e.target.value })}
                        placeholder="Notes, stops, highlights..."
                        rows={4}
                    />
                </label>

                {/* TAGS UI */}
                <div style={{ marginTop: 8 }} className="field">
                    <div className="label">Tags (optional)</div>

                    <div style={{ marginTop: 6 }}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                            {suggestedTags.map((s) => {
                                const normalized = String(s).trim().toLowerCase();
                                const isUsed = tags.includes(normalized);
                                return (
                                    <button
                                        key={normalized}
                                        type="button"
                                        className={`suggested-tag ${isUsed ? "used" : ""}`}
                                        onClick={() => !isUsed && addTag(normalized)}
                                        disabled={isUsed}
                                        aria-pressed={isUsed}
                                        title={isUsed ? "Already added" : `Add #${normalized}`}
                                        style={{ marginRight: 8, marginBottom: 8 }}
                                    >
                                        #{normalized}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="tag-chips" aria-hidden style={{ marginBottom: 8 }}>
                            {tags.map((t) => (
                                <span key={t} className="tag-chip" style={{ marginRight: 6, marginBottom: 6 }}>
                                    {t}
                                    <button type="button" className="tag-remove" onClick={() => removeTag(t)} aria-label={`Remove ${t}`} style={{ marginLeft: 6 }}>
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>

                        <input
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={onTagKeyDown}
                            placeholder={tags.length ? "Add another tag and press Enter" : "Add tags (press Enter or comma)"}
                            aria-label="Add tag"
                        />

                        <div style={{ marginTop: 6, fontSize: 13, color: 'var(--muted)' }}>
                            {tags.length}/{MAX_TAGS} tags • stored as plain strings. Max {MAX_TAG_LENGTH} chars.
                        </div>

                        {tagError && (
                            <div style={{ color: "#ff6b6b", marginTop: 6, fontSize: 13 }}>
                                {tagError}
                            </div>
                        )}
                    </div>
                </div>

                <div className="field" style={{ marginTop: 8 }}>
                    <div className="label">Map preview</div>

                    <small className="muted" style={{ display: "block", marginBottom: 6 }}>
                        The map below updates automatically (destination preferred). Paste an iframe tag or the iframe <code>src</code> URL here to override the map preview.
                    </small>

                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                        <input
                            type="text"
                            placeholder='Paste iframe tag or src URL to override (optional)'
                            value={customMapSrc}
                            onChange={(e) => setCustomMapSrc(e.target.value)}
                            style={{ flex: 1 }}
                        />
                        <button type="button" onClick={clearCustomMap} className="btn-cancel" disabled={!customMapSrc}>
                            Clear
                        </button>
                    </div>

                    <div style={{ border: "1px solid var(--soft-border)", borderRadius: 8, overflow: "hidden" }}>
                        {finalMapSrc ? (
                            <iframe title="trip-map" src={finalMapSrc} style={{ width: "100%", height: 300, border: 0 }} loading="lazy" />
                        ) : (
                            <div className="muted" style={{ padding: 12 }}>
                                No map available. Enter a destination or start location above, or paste a valid iframe URL.
                            </div>
                        )}
                    </div>

                    <small className="muted" style={{ display: "block", marginTop: 6 }}>
                        Note: only @handles are accepted for restricted visibility.
                    </small>
                </div>

                <div className="modal-actions" style={{ marginTop: 12, display: "flex", gap: 8 }}>
                    <button type="button" className="btn-cancel" onClick={handleClose} disabled={saving || deleting}>
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="btn-delete"
                        onClick={confirmAndDelete}
                        disabled={deleting || saving}
                        title="Delete this trip (permanent)"
                        aria-disabled={deleting || saving}
                    >
                        {deleting ? "Deleting…" : "Delete"}
                    </button>

                    <button
                        type="submit"
                        className="btn-save"
                        disabled={
                            saving
                            || deleting
                            || resolvingAllowedUsers
                            || Boolean(Object.keys(errors).length)
                        }
                        title={Object.keys(errors).length ? "Fix validation errors first" : "Save changes"}
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
